import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import { faStar as fasStar } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerModule } from 'ngx-spinner';
import { takeUntil } from 'rxjs/operators';
import { TchatRoomMessagesComponent } from 'src/app/components/tchat/tchat-room-messages/tchat-room-messages.component';
import { BaseDirective } from 'src/app/core/directives/base.directive';
import { DownloadHelper } from 'src/app/helpers/download-helper';
import { DocumentType } from 'src/app/models/enums/document-type';
import { FileType } from 'src/app/models/enums/file-type';
import { CommentViewerRefreshEvent } from 'src/app/models/events/comment-viewer-refresh-event';
import { Attachment } from 'src/app/models/server/attachment';
import { CommentInput } from 'src/app/models/server/comment-input';
import { ViewType } from 'src/app/models/server/enums/view-type';
import { FolderDocument } from 'src/app/models/server/folder-document';
import { SpinnerService } from 'src/app/services/core/spinner.service';
import { ViewerEventsService } from 'src/app/services/events/viewer-events.service';
import { FolderCommentsServiceProvider } from 'src/app/services/providers/folder-comments-service.provider';
import { FoldersServiceProvider } from 'src/app/services/providers/folders-service.provider';
import { DocumentsStateService } from 'src/app/services/state/documents-state.service';
import { FavoriteDocumentsStateService } from 'src/app/services/state/favorite-documents-state.service';
import { AttachmentComponent } from './attachment/attachment.component';
import { ViewerContext } from './models/viewer-context';
import { ViewerDisplayMode } from './models/viewer-display-mode';
import { CommentViewerComponent } from './viewers/comment-viewer/comment-viewer.component';
import { EmailViewerComponent } from './viewers/email-viewer/email-viewer.component';
import { ImageViewerComponent } from './viewers/image-viewer/image-viewer.component';
import { SmsViewerComponent } from './viewers/sms-viewer/sms-viewer.component';

@Component({
    selector: 'app-viewer-section',
    templateUrl: './viewer-section.component.html',
    styleUrls: ['./viewer-section.component.scss'],
    imports: [
        NgClass,
        MatButtonModule,
        MatTooltipModule,
        TchatRoomMessagesComponent,
        CommentViewerComponent,
        NgxSpinnerModule,
        MatIconModule,
        ImageViewerComponent,
        EmailViewerComponent,
        AttachmentComponent,
        SmsViewerComponent,
    ]
})
export class ViewerSectionComponent extends BaseDirective implements OnInit {
    /**
     * Références des icônes utilisés dans le template.
     */
    public favorite = fasStar;
    public nonfavorite = farStar;

    /**
     * Références des Enums utilisées dans le template.
     */
    public FileType = FileType;
    public DocumentType = DocumentType;

    /**
     * Valeur indiquant si le composant est affiché en mode consultation.
     */
    @Input()
    public readonly: boolean;

    /**
     * Id du dossier auquel appartient le document à afficher dans le visualisateur.
     */
    @Input()
    public folderId: string;

    /**
     * Type de la vue.
     */
    @Input()
    viewType: ViewType;

    /**
     * Id de la vue.
     */
    @Input()
    viewId: number;

    /**
     * Emis lorsque l'utilisateur édite une ligne de commentaire.
     */
    @Output()
    public commentEdited: EventEmitter<CommentInput> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur supprime une ligne de commentaire.
     */
    @Output()
    public commentDeleted: EventEmitter<CommentInput> = new EventEmitter();

    /**
     * Document affiché dans le visualisateur.
     */
    public document: FolderDocument;

    /**
     * Pièce-jointe actuellement sélectionnée pour être visualisée.
     */
    public selectedAttachment: Attachment = null;

    /**
     * Contexte du visualisateur.
     */
    public context: ViewerContext = new ViewerContext(ViewerDisplayMode.None);

    /**
     * Valeur indiquant si le fichier a été sélectionné par défaut (c.a.d pas par un clic utilisateur sur le fichier).
     */
    public defaultSelection: Boolean = true;

    /**
     * Valeur indiquant que le visualisateur est en cours de chargement.
     */
    public loading = false;

    /**
     * Types MIME autorisés à être prévisualisés.
     */
    private previewAuthorizedMimeTypes = ['application/pdf', 'text/plain', 'image/png', 'image/jpg', 'text/html', 'image/bmp', 'image/gif', 'image/pipeg', 'image/svg+xml'];

    private viewerAssociatedWithImageTypes = new Map<string, ViewerDisplayMode>();

    /**
     * Constructeur.
     * @param foldersService
     * @param sanitizer
     * @param ngZone
     */
    constructor(
        private foldersServiceProvider: FoldersServiceProvider,
        private commentsServiceProvider: FolderCommentsServiceProvider,
        private downloadHelper: DownloadHelper,
        public sanitizer: DomSanitizer,
        private cd: ChangeDetectorRef,
        private ngZone: NgZone,
        private spinner: SpinnerService,
        protected documentsStateService: DocumentsStateService,
        private favoriteStateService: FavoriteDocumentsStateService,
        private viewerEventsService: ViewerEventsService,
        protected cdRef: ChangeDetectorRef
    ) {
        super();
        this.viewerAssociatedWithImageTypes.set('application/pdf', ViewerDisplayMode.Pdf);
        this.viewerAssociatedWithImageTypes.set('text/plain', ViewerDisplayMode.Text);
        this.viewerAssociatedWithImageTypes.set('image/png', ViewerDisplayMode.Image);
        this.viewerAssociatedWithImageTypes.set('image/jpg', ViewerDisplayMode.Image);
        this.viewerAssociatedWithImageTypes.set('text/html', ViewerDisplayMode.Text);
        this.viewerAssociatedWithImageTypes.set('image/bmp', ViewerDisplayMode.Image);
        this.viewerAssociatedWithImageTypes.set('image/gif', ViewerDisplayMode.Image);
        this.viewerAssociatedWithImageTypes.set('image/pipeg', ViewerDisplayMode.Image);
        this.viewerAssociatedWithImageTypes.set('image/svg+xml', ViewerDisplayMode.Image);
    }

    ngOnInit(): void {
        this.subscribeToEvents();
    }

    /**
     * Appelée lorsque l'utilisateur sélectionne une pièce-jointe.
     * @param attachment : Instance de la pièce-jointe.
     */
    public onAttachmentSelected(attachment: Attachment) {
        this.selectAttachment(attachment, false);
    }

    /**
     * Appelée lorsque l'utilisateur demande à télécharger la pièce-jointe.
     */
    public onAttachmentDownloadRequested(): void {
        this.downloadAttachment(this.document.id, this.selectedAttachment.id);
    }

    /**
     * Appelée lorsqu'une pièce-jointe est ajouté au favoris ou retirer des favoris.
     * @param attachment : Instance de la pièce-jointe.
     */
    public onFavoriteStateChanged(attachment: Attachment): void {
        if (attachment.isFavorite === true) {
            this.favoriteStateService.AddFavoriteAttachment(this.viewId, this.document.folderId, attachment);
        } else {
            this.favoriteStateService.RemoveFavoriteAttachment(this.viewId, this.document.folderId, attachment);
        }
    }

    /**
     * Appelée lorsque l'utilisateur édite une ligne de commentaire.
     */
    public onEditComment(comment: CommentInput): void {
        this.commentEdited.next(comment);
    }

    /**
     * Appelée lorsque l'utilisateur supprime une ligne de  commentaire.
     */
    public onDeleteComment(comment: CommentInput): void {
        this.commentDeleted.next(comment);
    }

    /**
     * * S'abonne aux événements des autres composants.
     */
    private subscribeToEvents(): void {
        this.subscribeToDocumentSelectionChangeEvents();
        this.subscribeToCommentChangeEvents();
    }

    /**
     * S'abonne aux événements de changements (modification / suppression) des commentaires.
     */
    private subscribeToCommentChangeEvents() {
        this.viewerEventsService.commentChanged.pipe(takeUntil(this.componentDestroyed$)).subscribe((refreshEvent: CommentViewerRefreshEvent) => {
            // Si le visualisateur affiche les commentaires pour lesquels un changement a eu lieu, alors le visualisateur est mis à jour
            if (this.document && this.document.type === DocumentType.Comment && refreshEvent.commentDocument.id === this.document.id) {
                this.resetViewer();
                this.document = refreshEvent.commentDocument;

                this.selectDefaultFile();
            }
        });
    }

    /**
     * S'abonne aux événements de changement de document selectionné.
     */
    private subscribeToDocumentSelectionChangeEvents() {
        this.documentsStateService.onSelectedDocumentChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((documentsDictionary) => {
            // TODO : modifier pour recevoir un evenement avec uniquement le dossier et le document concerné
            if (!!documentsDictionary[this.folderId]) {
                // Si l'evenement concerne ce dossier.
                if (!this.document || documentsDictionary[this.folderId].id != this.document.id || this.document.type === DocumentType.Comment) {
                    // Si le nouveau document selectionné est different du document actuellement visaulisé ou si aucun document n'est actuellement visualisé.
                    this.resetViewer();
                    this.document = documentsDictionary[this.folderId];

                    this.selectDefaultFile();
                }
            } else {
                // Réinitialise le visualisateur.
                this.resetViewer();
                this.loading = false;
                this.spinner.hide('viewer-spinner');
            }
        });
    }

    /**
     * Affiche le contenu de la conversation chat sélectionnée.
     * @param defaultSelection : Valeur indiquant s'il s'agit d'une selection par defaut (true) ou d'un changement de document (false).
     */
    private displayChat(defaultSelection: boolean) {
        this.defaultSelection = defaultSelection;
        this.foldersServiceProvider
            .getService(this.viewType)
            .room(this.folderId, this.document.id, this.selectedAttachment.id)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x) => {
                this.context = new ViewerContext(ViewerDisplayMode.Tchat);
                this.context.room = x;

                this.loading = false;
                this.spinner.hide('viewer-spinner');
            });
    }

    /**
     * Affiche le contenu du document commentaire selectionné.
     * @param commentType : Type de commentaire contenu par le document.
     * @param defaultSelection : Valeur indiquant s'il s'agit d'une selection par defaut (true) ou d'un changement de document (false).
     */
    private displayComment(commentType: DocumentType, defaultSelection: boolean) {
        this.defaultSelection = defaultSelection;

        this.commentsServiceProvider
            .getService(this.viewType)
            .getCommentContent(this.viewId, this.folderId, this.document.id, this.selectedAttachment.id)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((comments) => {
                let context = new ViewerContext(ViewerDisplayMode.Comment);
                context.comments = comments;
                context.commentType = commentType;

                this.context = context;
                this.cdRef.detectChanges();
                this.loading = false;
                this.spinner.hide('viewer-spinner');
            });
    }

    /**
     * Affiche le contenu d'un fichier s'il est visualisable ou sinon le télécharge.
     * @param documentId : Id du document auquel appartient la pièce-jointe.
     * @param AttachmentId : Id de la pièce-jointe à visualiser ou télécharger.
     * @param defaultSelection : Valeur indiquant s'il s'agit d'une selection par defaut (true) ou d'un changement de document (false).
     */
    private displayOrDownloadFile(documentId: string, AttachmentId: string, defaultSelection: boolean) {
        this.foldersServiceProvider
            .getService(this.viewType)
            .download(this.viewId, this.document.folderId, documentId, AttachmentId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((blob) => {
                if (this.previewAuthorizedMimeTypes.includes(blob.type)) {
                    // Si le type mime de la pièce-jointe récupérée le permet, la pièce-jointe est visualisée.
                    this.displayBlobFile(blob, this.viewerAssociatedWithImageTypes.get(blob.type), defaultSelection);
                } else {
                    // Sinon elle est téléchargée.
                    this.downloadBlobFile(blob, defaultSelection);
                }
            });
    }

    /**
     * Affiche le contenu de la pièce-jointe contenant le corps du mail.
     * @param documentId : Id du document auquel appartient la pièce-jointe.
     * @param attachmentId : Id de la pièce-jointe à visualiser.
     * @param defaultSelection : Valeur indiquant s'il s'agit d'une selection par defaut (true) ou d'un changement de document (false).
     */
    private displayEmailBodyFile(documentId: string, attachmentId: string, defaultSelection: boolean) {
        this.foldersServiceProvider
            .getService(this.viewType)
            .download(this.viewId, this.document.folderId, documentId, attachmentId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((blob) => {
                this.displayBlobFile(blob, ViewerDisplayMode.Email, defaultSelection);
            });
    }

    /**
     * Télécharge le Blob contenant la pièce-jointe.
     * @param blob : Contenu de la pièce-jointe au format binaire.
     * @param displayMode : Mode d'affichage de la pièce-jointe.
     * @param defaultSelection : Valeur indiquant s'il s'agit d'une selection par defaut (true) ou d'un changement de document (false). document.
     */
    private displayBlobFile(blob: Blob, displayMode: ViewerDisplayMode, defaultSelection: boolean) {
        this.ngZone.runOutsideAngular(() => {
            this.defaultSelection = defaultSelection;
            this.context = new ViewerContext(displayMode);

            switch (displayMode) {
                case ViewerDisplayMode.Text:
                case ViewerDisplayMode.Email:
                    blob.text().then((text) => {
                        this.context.textFileContent = text;
                        this.cdRef.detectChanges();
                    });
                    break;
                case ViewerDisplayMode.Image:
                    const imageUrl = URL.createObjectURL(blob);
                    this.context.safeFileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(imageUrl);
                    this.context.isImageFile = true;
                    break;
                case ViewerDisplayMode.Pdf:
                    const pdfUrl = URL.createObjectURL(blob);
                    this.context.safeFileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);
                    this.context.isImageFile = false;
                    break;
            }

            this.loading = false;
            this.spinner.hide('viewer-spinner');
        });

        this.cd.detectChanges();
    }

    /**
     * Télécharge la pièce-jointe spécifiée.
     * @param blob : Contenu de la pièce-jointe au format binaire.
     * @param defaultSelection : Valeur indiqu s'il s'agit d'une selection par defaut (true) ou d'un changement de document (false).     */
    private downloadBlobFile(blob: Blob, defaultSelection: boolean) {
        this.defaultSelection = defaultSelection;

        this.context = new ViewerContext(ViewerDisplayMode.None);

        if (!defaultSelection) {
            // S'il s'agit d'une selection par défaut, la pièce-jointe n'est pas télécharger automatiquement.
            this.downloadHelper.downloadFile(blob, this.selectedAttachment.name);
        }

        this.loading = false;
        this.spinner.hide('viewer-spinner');
    }

    /**
     * Téléchargement la pièce-jointe spécifiée.
     * @param documentId : Id du document auquel appartient la pièce-jointe.
     * @param attachmentId : Id de la pièce-jointe.
     */
    private downloadAttachment(documentId: string, attachmentId: string) {
        this.loading = true;
        this.spinner.show('viewer-spinner');
        this.foldersServiceProvider
            .getService(this.viewType)
            .download(this.viewId, this.document.folderId, documentId, attachmentId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((blob) => {
                this.downloadHelper.downloadFile(blob, this.selectedAttachment.name);

                this.loading = false;
                this.spinner.hide('viewer-spinner');
            });
    }

    /**
     * Réinitialise le visualisateur.
     */
    private resetViewer() {
        this.loading = true;
        this.spinner.show('viewer-spinner');
        this.document = null;
        this.selectedAttachment = null;
        this.defaultSelection = true;
    }

    /**
     * Selectionne la pièce-jointe par défaut du document.
     */
    private selectDefaultFile() {
        if (this.document.type == DocumentType.Email) {
            // Dans le cas d'un mail, la pièce-jointe par défaut est le corps du mail.
            const mailBodyFile = this.document.attachments.find((x) => x.name == 'body.txt' || x.name == 'Corps du mail');
            mailBodyFile.name = 'Corps du mail';
            this.selectEmailBodyAttachment(mailBodyFile, true);
        } else {
            // Sinon il s'agit du premier fichier du document.
            this.selectAttachment(this.document.attachments[0], true);
        }
    }

    /**
     * Sélectionne une pièce-jointe.
     * @param attachment : Instance de la pièce-jointe.
     * @param defaultSelection : Valeur indiquant s'il s'agit d'une selection par defaut (true) ou d'un changement de document (false).
     */
    private selectAttachment(attachment: Attachment, defaultSelection: boolean): void {
        this.selectedAttachment = attachment;

        switch (this.document.type) {
            case DocumentType.Tchat:
                this.displayChat(defaultSelection);
                break;
            case DocumentType.Comment:
            case DocumentType.MergedComment:
                this.displayComment(this.document.type, defaultSelection);
                break;
            case DocumentType.Favorite:
                this.displayOrDownloadFile(this.selectedAttachment.documentId, this.selectedAttachment.id, defaultSelection);
                break;
            default:
                this.displayOrDownloadFile(this.document.id, this.selectedAttachment.id, defaultSelection);
                break;
        }
    }

    /**
     * Sélectionne la pièce-jointe contenant le corps du mail.
     * @param attachment : Instance de la pièce-jointe.
     * @param defaultSelection : Valeur indiquant s'il s'agit d'une selection par defaut (true) ou d'un changement de document (false).
     */
    private selectEmailBodyAttachment(attachment: Attachment, defaultSelection: boolean): void {
        this.selectedAttachment = attachment;
        this.displayEmailBodyFile(this.document.id, this.selectedAttachment.id, defaultSelection);
    }
}
